import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, X, User, Users, UserCheck, UserX, AlertTriangle, Filter, Info, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, Download, FileSpreadsheet, Briefcase } from 'lucide-react';
import AdolescentProfile from '@/components/AdolescentProfile';
import AffinitySelector from '@/components/AffinitySelector';
import PhotoUpload from '@/components/PhotoUpload';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

const Adolescents = () => {
  const { currentMinistry, getEntityName, loading: ministryLoading } = useMinistry();
  const [adolescents, setAdolescents] = useState([]);
  const [filteredAdolescents, setFilteredAdolescents] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [serviceAreaCategories, setServiceAreaCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedAdolescentId, setSelectedAdolescentId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  
  // Filtros
  const [genderFilter, setGenderFilter] = useState(['masculino', 'feminino']);
  const [engagementFilter, setEngagementFilter] = useState(['ativo', 'desengajado', 'inativo']);
  const [statusFilter, setStatusFilter] = useState(['membro', 'visitante', 'visitante_primeira_vez']);
  const [ageFilter, setAgeFilter] = useState('all');
  const [pgFilter, setPgFilter] = useState('all'); // 'all', 'com_pg', 'sem_pg'
  
  // Ordenação
  const [sortColumn, setSortColumn] = useState('name'); // Coluna padrão: nome
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ou 'desc'
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    masculino: 0,
    feminino: 0,
    ativos: 0,
    desengajados: 0,
    inativos: 0,
    membros: 0,
    visitantes: 0,
    activeInPg: 0,
    activeWithoutPg: 0
  });
  
  // Engagement criteria for tooltips
  const [engagementCriteria, setEngagementCriteria] = useState(null);
  
  // PG member IDs
  const [membersInPgs, setMembersInPgs] = useState(new Set());
  
  // Small Groups data and adolescent-to-PG mapping
  const [smallGroups, setSmallGroups] = useState([]);
  const [adolescentPgMap, setAdolescentPgMap] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    cpf: '',
    phone: '',
    gender: 'masculino',
    guardian_name: '',
    guardian_phone: '',
    guardian_relationship: '',
    guardian2_name: '',
    guardian2_phone: '',
    has_special_needs: false,
    has_completed_immersion: false,
    immersion_date: '',
    notes: '',
    status: 'visitante_primeira_vez',
    engagement_status: 'ativo',
    engagement_status_manual: false,
    photo_url: '',
    affinity_ids: [],
    service_area_ids: []
  });

    // Debounce da busca
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchAdolescents();
    } else if (!ministryLoading && !currentMinistry) {
      setLoading(false);
    }
  }, [currentMinistry?.id, ministryLoading]);

  useEffect(() => {
    fetchEngagementCriteria();
  }, []);

  const fetchEngagementCriteria = async () => {
    try {
      const response = await axios.get(`${API}/settings/engagement-criteria`);
      setEngagementCriteria(response.data);
    } catch (error) {
      console.error('Error fetching engagement criteria:', error);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [adolescents, debouncedSearch, genderFilter, engagementFilter, statusFilter, ageFilter, pgFilter, membersInPgs, sortColumn, sortDirection]);

  const fetchAdolescents = async () => {
    if (!currentMinistry?.id) return;
    
    try {
      const [adolescentsRes, groupsRes, areasRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/adolescents`, {
          params: {
            ministry_id: currentMinistry.id,
          },
        }),
        axios.get(`${API}/small-groups?ministry_id=${currentMinistry.id}`),
        axios.get(`${API}/service-areas`),
        axios.get(`${API}/service-area-categories`)
      ]);
      
      // Criar set de IDs de membros em PGs REGULARES (não temporários/visitantes)
      const isVisitantesPG = (group) => group.is_temporary || group.name?.toLowerCase().includes('visitante');
      const membersSet = new Set();
      groupsRes.data
        .filter(group => !isVisitantesPG(group)) // Ignora PGs temporários e de visitantes
        .forEach(group => {
          (group.member_ids || []).forEach(id => membersSet.add(id));
        });
      setMembersInPgs(membersSet);
      setServiceAreas(areasRes.data);
      setServiceAreaCategories(categoriesRes.data);
      
      // Store groups and create adolescent-to-PG mapping
      setSmallGroups(groupsRes.data);
      const pgMap = {};
      groupsRes.data.forEach(group => {
        (group.member_ids || []).forEach(memberId => {
          if (!pgMap[memberId]) {
            pgMap[memberId] = [];
          }
          pgMap[memberId].push({
            id: group.id,
            name: group.name,
            is_temporary: group.is_temporary || false,
            is_visitantes: isVisitantesPG(group)
          });
        });
      });
      setAdolescentPgMap(pgMap);
      
      setAdolescents(adolescentsRes.data);
      calculateStats(adolescentsRes.data, membersSet);
    } catch (error) {
      toast.error('Erro ao carregar adolescentes');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data, membersSet = membersInPgs) => {
    const ativos = data.filter(a => a.engagement_status === 'ativo');
    const activeInPg = ativos.filter(a => membersSet.has(a.id)).length;
    const activeWithoutPg = ativos.length - activeInPg;
    
    setStats({
      total: data.length,
      masculino: data.filter(a => a.gender === 'masculino').length,
      feminino: data.filter(a => a.gender === 'feminino').length,
      ativos: ativos.length,
      desengajados: data.filter(a => a.engagement_status === 'desengajado').length,
      inativos: data.filter(a => a.engagement_status === 'inativo').length,
      membros: data.filter(a => a.status === 'membro').length,
      visitantes: data.filter(a => a.status === 'visitante' || a.status === 'visitante_primeira_vez').length,
      activeInPg,
      activeWithoutPg
    });
  };

  const highlightMatch = (text, term) => {
    if (!text || !term) return text;

    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escaped) return text;

    const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) => (
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} className="bg-amber-200 text-inherit rounded px-0.5">{part}</mark>
        : part
    ));
  };

  const applyFilters = () => {
    let result = [...adolescents];
    const effectiveSearch = debouncedSearch;
    
    // Busca por texto
    if (effectiveSearch) {
      const searchLower = effectiveSearch.toLowerCase();
      result = result.filter(a => 
        a.name.toLowerCase().includes(searchLower) ||
        a.cpf?.includes(effectiveSearch) ||
        a.phone?.includes(effectiveSearch) ||
        a.guardian_name?.toLowerCase().includes(searchLower) ||
        a.guardian_phone?.includes(effectiveSearch) ||
        a.guardian2_name?.toLowerCase().includes(searchLower) ||
        a.guardian2_phone?.includes(effectiveSearch)
      );
    }
    
    // Filtro de gênero (array)
    if (genderFilter.length > 0 && genderFilter.length < 2) {
      result = result.filter(a => genderFilter.includes(a.gender));
    }
    
    // Filtro de engajamento (array)
    if (engagementFilter.length > 0 && engagementFilter.length < 3) {
      result = result.filter(a => engagementFilter.includes(a.engagement_status));
    }
    
    // Filtro de status (array)
    if (statusFilter.length > 0 && statusFilter.length < 3) {
      result = result.filter(a => statusFilter.includes(a.status));
    }
    
    // Filtro de idade
    if (ageFilter !== 'all') {
      const age = parseInt(ageFilter);
      result = result.filter(a => a.age === age);
    }
    
    // Filtro de PG
    if (pgFilter === 'com_pg') {
      result = result.filter(a => membersInPgs.has(a.id));
    } else if (pgFilter === 'sem_pg') {
      result = result.filter(a => !membersInPgs.has(a.id));
    }
    
    // Ordenação
    if (sortColumn) {
      result.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortColumn) {
          case 'name':
            valueA = (a.name || '').toLowerCase();
            valueB = (b.name || '').toLowerCase();
            break;
          case 'age':
            valueA = a.age || 0;
            valueB = b.age || 0;
            break;
          case 'gender':
            valueA = a.gender || '';
            valueB = b.gender || '';
            break;
          case 'phone':
            valueA = a.phone || '';
            valueB = b.phone || '';
            break;
          case 'status':
            valueA = a.status || '';
            valueB = b.status || '';
            break;
          case 'engagement':
            // Ordem: ativo > desengajado > inativo
            const engagementOrder = { 'ativo': 1, 'desengajado': 2, 'inativo': 3 };
            valueA = engagementOrder[a.engagement_status] || 4;
            valueB = engagementOrder[b.engagement_status] || 4;
            break;
          case 'checkins':
            valueA = a.total_checkins || 0;
            valueB = b.total_checkins || 0;
            break;
          default:
            valueA = '';
            valueB = '';
        }
        
        // Comparação
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredAdolescents(result);
  };

  const clearFilters = () => {
    setSearch('');
    setGenderFilter(['masculino', 'feminino']);
    setEngagementFilter(['ativo', 'desengajado', 'inativo']);
    setStatusFilter(['membro', 'visitante', 'visitante_primeira_vez']);
    setAgeFilter('all');
    setPgFilter('all');
  };

  // Função para ordenar ao clicar no cabeçalho
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Se já está ordenando por esta coluna, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nova coluna, começa com ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Limpar ordenação (volta ao padrão: nome ascendente)
  const clearSort = () => {
    setSortColumn('name');
    setSortDirection('asc');
  };

  // Função para renderizar ícone de ordenação
  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="ml-1 text-blue-600" />
      : <ArrowDown size={14} className="ml-1 text-blue-600" />;
  };

  // Verificar se há ordenação diferente do padrão
  const hasCustomSort = sortColumn !== 'name' || sortDirection !== 'asc';
  const isSearching = search.trim() !== debouncedSearch;

  const hasActiveFilters = search || 
    genderFilter.length < 2 || 
    engagementFilter.length < 3 || 
    statusFilter.length < 3 || 
    ageFilter !== 'all' ||
    pgFilter !== 'all';

  // Função para exportar para CSV
  const exportToCSV = () => {
    if (filteredAdolescents.length === 0) {
      toast.error('Nenhum adolescente para exportar');
      return;
    }

    // Cabeçalhos do CSV
    const headers = ['Nome', 'Idade', 'Data Nascimento', 'Gênero', 'Telefone', 'CPF', 'Status', 'Engajamento', 'Presenças', 'Taxa Presença (%)', 'Responsável', 'Tel. Responsável', 'Necessidades Especiais', 'Observações'];
    
    // Dados
    const rows = filteredAdolescents.map(a => [
      a.name || '',
      a.age || '',
      a.birth_date || '',
      a.gender === 'masculino' ? 'Masculino' : 'Feminino',
      a.phone || '',
      a.cpf || '',
      a.status === 'membro' ? 'Membro' : a.status === 'visitante' ? 'Visitante' : 'Visitante 1ª vez',
      a.engagement_status === 'ativo' ? 'Ativo' : a.engagement_status === 'desengajado' ? 'Desengajado' : 'Inativo',
      a.total_checkins || 0,
      a.attendance_rate || 0,
      a.guardian_name || '',
      a.guardian_phone || '',
      a.has_special_needs ? 'Sim' : 'Não',
      (a.notes || '').replace(/[\n\r]/g, ' ')
    ]);

    // Criar conteúdo CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Adicionar BOM para UTF-8 (para Excel reconhecer acentos)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Criar link de download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `adolescentes_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${filteredAdolescents.length} adolescente(s) exportado(s) para CSV`);
  };

  // Função para exportar para Excel (XLSX via HTML table)
  const exportToExcel = () => {
    if (filteredAdolescents.length === 0) {
      toast.error('Nenhum adolescente para exportar');
      return;
    }

    // Criar tabela HTML
    let tableHTML = `
      <table>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Idade</th>
            <th>Data Nascimento</th>
            <th>Gênero</th>
            <th>Telefone</th>
            <th>CPF</th>
            <th>Status</th>
            <th>Engajamento</th>
            <th>Presenças</th>
            <th>Taxa Presença (%)</th>
            <th>Responsável</th>
            <th>Tel. Responsável</th>
            <th>Necessidades Especiais</th>
            <th>Observações</th>
          </tr>
        </thead>
        <tbody>
    `;

    filteredAdolescents.forEach(a => {
      tableHTML += `
        <tr>
          <td>${a.name || ''}</td>
          <td>${a.age || ''}</td>
          <td>${a.birth_date || ''}</td>
          <td>${a.gender === 'masculino' ? 'Masculino' : 'Feminino'}</td>
          <td>${a.phone || ''}</td>
          <td>${a.cpf || ''}</td>
          <td>${a.status === 'membro' ? 'Membro' : a.status === 'visitante' ? 'Visitante' : 'Visitante 1ª vez'}</td>
          <td>${a.engagement_status === 'ativo' ? 'Ativo' : a.engagement_status === 'desengajado' ? 'Desengajado' : 'Inativo'}</td>
          <td>${a.total_checkins || 0}</td>
          <td>${a.attendance_rate || 0}</td>
          <td>${a.guardian_name || ''}</td>
          <td>${a.guardian_phone || ''}</td>
          <td>${a.has_special_needs ? 'Sim' : 'Não'}</td>
          <td>${(a.notes || '').replace(/[\n\r]/g, ' ')}</td>
        </tr>
      `;
    });

    tableHTML += '</tbody></table>';

    // Criar arquivo Excel
    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `adolescentes_${new Date().toISOString().split('T')[0]}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${filteredAdolescents.length} adolescente(s) exportado(s) para Excel`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        ministry_id: currentMinistry?.id
      };
      
      if (editingId) {
        await axios.put(`${API}/adolescents/${editingId}`, dataToSend);
        toast.success(`${getEntityName()} atualizado com sucesso!`);
      } else {
        await axios.post(`${API}/adolescents`, dataToSend);
        toast.success(`${getEntityName()} cadastrado com sucesso!`);
      }
      setDialogOpen(false);
      resetForm();
      fetchAdolescents();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao salvar ${getEntityName().toLowerCase()}`);
    }
  };

  const handleEdit = (adolescent) => {
    setEditingId(adolescent.id);
    setFormData({
      name: adolescent.name,
      birth_date: adolescent.birth_date,
      cpf: adolescent.cpf,
      phone: adolescent.phone,
      gender: adolescent.gender,
      guardian_name: adolescent.guardian_name,
      guardian_phone: adolescent.guardian_phone,
      guardian_relationship: adolescent.guardian_relationship || '',
      guardian2_name: adolescent.guardian2_name || '',
      guardian2_phone: adolescent.guardian2_phone || '',
      has_special_needs: adolescent.has_special_needs || false,
      has_completed_immersion: adolescent.has_completed_immersion || false,
      immersion_date: adolescent.immersion_date || '',
      notes: adolescent.notes || '',
      status: adolescent.status,
      engagement_status: adolescent.engagement_status || 'ativo',
      engagement_status_manual: adolescent.engagement_status_manual || false,
      photo_url: adolescent.photo_url || '',
      affinity_ids: adolescent.affinity_ids || [],
      service_area_ids: adolescent.service_area_ids || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este adolescente?')) return;
    try {
      await axios.delete(`${API}/adolescents/${id}`);
      toast.success('Adolescente excluído com sucesso!');
      fetchAdolescents();
    } catch (error) {
      toast.error('Erro ao excluir adolescente');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      birth_date: '',
      cpf: '',
      phone: '',
      gender: 'masculino',
      guardian_name: '',
      guardian_phone: '',
      guardian_relationship: '',
      guardian2_name: '',
      guardian2_phone: '',
      has_special_needs: false,
      has_completed_immersion: false,
      immersion_date: '',
      notes: '',
      status: 'visitante_primeira_vez',
      engagement_status: 'ativo',
      engagement_status_manual: false,
      photo_url: '',
      affinity_ids: [],
      service_area_ids: []
    });
  };

  const handleViewProfile = (id) => {
    setSelectedAdolescentId(id);
    setProfileDialogOpen(true);
  };

  const getEngagementBadge = (status) => {
    const config = {
      'ativo': { color: 'bg-green-100 text-green-800', label: 'Ativo' },
      'desengajado': { color: 'bg-amber-100 text-amber-800', label: 'Desengajado' },
      'inativo': { color: 'bg-red-100 text-red-800', label: 'Inativo' }
    };
    const c = config[status] || config['ativo'];
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>{c.label}</span>;
  };

  // Render volunteer service areas for an adolescent
  const renderServiceAreas = (adolescent) => {
    const areaIds = adolescent.service_area_ids || [];
    if (areaIds.length === 0) {
      return <span className="text-gray-400 text-xs italic">Não serve</span>;
    }
    const areas = areaIds.map(id => serviceAreas.find(a => a.id === id)).filter(Boolean);
    if (areas.length === 0) {
      return <span className="text-gray-400 text-xs italic">Não serve</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {areas.slice(0, 2).map(area => (
          <span key={area.id} className="px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
            {area.name}
          </span>
        ))}
        {areas.length > 2 && (
          <span className="text-xs text-gray-500">+{areas.length - 2}</span>
        )}
      </div>
    );
  };

  // Render PG info for an adolescent
  const renderPgInfo = (adolescent) => {
    const pgs = adolescentPgMap[adolescent.id] || [];
    if (pgs.length === 0) {
      return <span className="text-orange-500 text-xs font-medium">Sem PG</span>;
    }
    // Check if all PGs are temporary (visitor PGs)
    const regularPgs = pgs.filter(pg => !pg.is_temporary);
    const temporaryPgs = pgs.filter(pg => pg.is_temporary);
    
    if (regularPgs.length === 0 && temporaryPgs.length > 0) {
      return <span className="text-amber-600 text-xs font-medium">PG Visitante</span>;
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {regularPgs.slice(0, 1).map(pg => (
          <span key={pg.id} className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
            {pg.name.replace('PG ', '').replace('PG da ', '').replace('PG do ', '')}
          </span>
        ))}
        {regularPgs.length > 1 && (
          <span className="text-xs text-gray-500">+{regularPgs.length - 1}</span>
        )}
      </div>
    );
  };

  // Get unique ages for filter
  const uniqueAges = [...new Set(adolescents.map(a => a.age).filter(age => age != null && !isNaN(age)))].sort((a, b) => a - b);

  return (
    <TooltipProvider>
    <div className="fade-in" data-testid="adolescents-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate" style={{fontFamily: 'Space Grotesk'}}>Adolescentes</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Gerencie os adolescentes do ministério</p>
        </div>
        
        <div className="flex gap-2 shrink-0">
          {/* Botão de Exportação */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex items-center justify-center px-3" data-testid="export-button">
                <Download size={18} />
                <ChevronDown size={16} className="ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48 p-2">
              <div className="space-y-1">
                <button
                  onClick={exportToCSV}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition"
                  data-testid="export-csv-button"
                >
                  <FileSpreadsheet size={16} className="text-green-600" />
                  Exportar CSV
                </button>
                <button
                  onClick={exportToExcel}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition"
                  data-testid="export-excel-button"
                >
                  <FileSpreadsheet size={16} className="text-blue-600" />
                  Exportar Excel
                </button>
              </div>
              {hasActiveFilters && (
                <p className="text-xs text-gray-500 mt-2 pt-2 border-t">
                  Exportando {filteredAdolescents.length} de {stats.total}
                </p>
              )}
            </PopoverContent>
          </Popover>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-1 px-3" data-testid="add-adolescent-button">
                <Plus size={18} />
                <span className="hidden sm:inline">Novo Adolescente</span>
                <span className="sm:hidden">Novo</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Adolescente' : 'Cadastrar Novo Adolescente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="adolescent-form">
              <PhotoUpload
                currentPhotoUrl={formData.photo_url}
                onPhotoChange={(url) => setFormData({...formData, photo_url: url})}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Nome Completo *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="adolescent-name-input" />
                </div>
                <div>
                  <Label>Data de Nascimento *</Label>
                  <Input type="date" value={formData.birth_date} onChange={(e) => setFormData({...formData, birth_date: e.target.value})} required data-testid="adolescent-birthdate-input" />
                </div>
                <div>
                  <Label>CPF</Label>
                  <Input value={formData.cpf} onChange={(e) => setFormData({...formData, cpf: e.target.value})} placeholder="000.000.000-00" data-testid="adolescent-cpf-input" />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="adolescent-phone-input" />
                </div>
                <div>
                  <Label>Gênero *</Label>
                  <Select value={formData.gender} onValueChange={(value) => setFormData({...formData, gender: value})}>
                    <SelectTrigger data-testid="adolescent-gender-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome do Responsável</Label>
                  <Input value={formData.guardian_name} onChange={(e) => setFormData({...formData, guardian_name: e.target.value})} data-testid="adolescent-guardian-input" />
                </div>
                <div>
                  <Label>Telefone do Responsável</Label>
                  <Input value={formData.guardian_phone} onChange={(e) => setFormData({...formData, guardian_phone: e.target.value})} data-testid="adolescent-guardian-phone-input" />
                </div>
                <div>
                  <Label>Grau de Parentesco</Label>
                  <Select value={formData.guardian_relationship || ''} onValueChange={(value) => setFormData({...formData, guardian_relationship: value})}>
                    <SelectTrigger data-testid="adolescent-guardian-relationship-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pai">Pai</SelectItem>
                      <SelectItem value="mae">Mãe</SelectItem>
                      <SelectItem value="avo">Avô/Avó</SelectItem>
                      <SelectItem value="tio">Tio/Tia</SelectItem>
                      <SelectItem value="irmao">Irmão/Irmã</SelectItem>
                      <SelectItem value="padrasto">Padrasto/Madrasta</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Responsável 2</Label>
                  <Input value={formData.guardian2_name || ''} onChange={(e) => setFormData({...formData, guardian2_name: e.target.value})} placeholder="Nome do segundo responsável" data-testid="adolescent-guardian2-input" />
                </div>
                <div>
                  <Label>Telefone Responsável 2</Label>
                  <Input value={formData.guardian2_phone || ''} onChange={(e) => setFormData({...formData, guardian2_phone: e.target.value})} placeholder="(00) 00000-0000" data-testid="adolescent-guardian2-phone-input" />
                </div>
                <div>
                  <Label>Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger data-testid="adolescent-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="membro">Membro</SelectItem>
                      <SelectItem value="visitante">Visitante</SelectItem>
                      <SelectItem value="visitante_primeira_vez">Visitante de Primeira Vez</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1 pr-10">
                    1º check-in → Visitante 1ª vez | 2+ check-ins → Visitante | Imersão → Membro
                  </p>
                </div>
                <div>
                  <Label>Engajamento</Label>
                  <Select 
                    value={formData.engagement_status} 
                    onValueChange={(value) => setFormData({
                      ...formData, 
                      engagement_status: value,
                      engagement_status_manual: true  // Marca como manual ao alterar
                    })}
                  >
                    <SelectTrigger data-testid="adolescent-engagement-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">🟢 Ativo</SelectItem>
                      <SelectItem value="desengajado">🟡 Desengajado</SelectItem>
                      <SelectItem value="inativo">🔴 Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.engagement_status_manual && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-amber-600">⚠️ Definido manualmente</span>
                      <button 
                        type="button" 
                        onClick={() => setFormData({...formData, engagement_status_manual: false})}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Restaurar automático
                      </button>
                    </div>
                  )}
                </div>
                {/* Imersão Pulse */}
                <div className="flex items-center gap-3 pt-6">
                  <input
                    type="checkbox"
                    id="has_completed_immersion"
                    checked={formData.has_completed_immersion || false}
                    onChange={(e) => setFormData({...formData, has_completed_immersion: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    data-testid="adolescent-immersion-checkbox"
                  />
                  <Label htmlFor="has_completed_immersion" className="cursor-pointer">
                    Participou da Imersão Pulse
                  </Label>
                </div>
                <div>
                  <Label>Data da Imersão</Label>
                  <Input 
                    type="date" 
                    value={formData.immersion_date || ''} 
                    onChange={(e) => setFormData({...formData, immersion_date: e.target.value})} 
                    disabled={!formData.has_completed_immersion}
                    className={!formData.has_completed_immersion ? 'bg-gray-100 cursor-not-allowed' : ''}
                    data-testid="adolescent-immersion-date-input"
                  />
                </div>
                
                {/* Necessidades Especiais e Observações */}
                <div className="col-span-2 flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="has_special_needs"
                    checked={formData.has_special_needs || false}
                    onChange={(e) => setFormData({...formData, has_special_needs: e.target.checked})}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="adolescent-special-needs-checkbox"
                  />
                  <Label htmlFor="has_special_needs" className="cursor-pointer">
                    Necessidades Especiais
                  </Label>
                </div>
                <div className="col-span-2">
                  <Label>Observações</Label>
                  <textarea 
                    value={formData.notes || ''} 
                    onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                    placeholder="Informações adicionais, alergias, detalhes sobre necessidades especiais, etc."
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="adolescent-notes-input"
                  />
                </div>
                
                {/* Áreas de Serviço */}
                <div className="col-span-2">
                  <Label className="flex items-center gap-2 mb-2">
                    <Briefcase size={16} className="text-purple-600" />
                    Áreas de Serviço (Voluntariado)
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full justify-between h-auto min-h-[40px] py-2"
                        type="button"
                        data-testid="service-areas-select"
                      >
                        <span className="text-left flex-1">
                          {formData.service_area_ids?.length > 0 ? (
                            <span className="flex flex-wrap gap-1">
                              {formData.service_area_ids.map(id => {
                                const area = serviceAreas.find(a => a.id === id);
                                return area ? (
                                  <span key={id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                                    {area.name}
                                  </span>
                                ) : null;
                              })}
                            </span>
                          ) : (
                            <span className="text-gray-500">Selecione as áreas de serviço...</span>
                          )}
                        </span>
                        <ChevronDown size={16} className="ml-2 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-80 p-0" 
                      align="start"
                    >
                      <div 
                        className="max-h-80 overflow-y-auto p-3"
                        onWheel={(e) => e.stopPropagation()}
                      >
                      {serviceAreaCategories.length > 0 ? (
                        <div className="space-y-4">
                          {serviceAreaCategories.map(category => {
                            const areasInCategory = serviceAreas.filter(a => a.category_id === category.id);
                            if (areasInCategory.length === 0) return null;
                            return (
                              <div key={category.id}>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                  {category.name}
                                </p>
                                <div className="space-y-1">
                                  {areasInCategory.map(area => (
                                    <label key={area.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                                      <Checkbox 
                                        checked={formData.service_area_ids?.includes(area.id)}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            setFormData({
                                              ...formData, 
                                              service_area_ids: [...(formData.service_area_ids || []), area.id]
                                            });
                                          } else {
                                            setFormData({
                                              ...formData, 
                                              service_area_ids: (formData.service_area_ids || []).filter(id => id !== area.id)
                                            });
                                          }
                                        }}
                                        data-testid={`service-area-checkbox-${area.id}`}
                                      />
                                      <span className="text-sm">{area.name}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {/* Áreas sem categoria */}
                          {serviceAreas.filter(a => !a.category_id).length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Outras Áreas
                              </p>
                              <div className="space-y-1">
                                {serviceAreas.filter(a => !a.category_id).map(area => (
                                  <label key={area.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                                    <Checkbox 
                                      checked={formData.service_area_ids?.includes(area.id)}
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setFormData({
                                            ...formData, 
                                            service_area_ids: [...(formData.service_area_ids || []), area.id]
                                          });
                                        } else {
                                          setFormData({
                                            ...formData, 
                                            service_area_ids: (formData.service_area_ids || []).filter(id => id !== area.id)
                                          });
                                        }
                                      }}
                                      data-testid={`service-area-checkbox-${area.id}`}
                                    />
                                    <span className="text-sm">{area.name}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : serviceAreas.length > 0 ? (
                        <div className="space-y-1">
                          {serviceAreas.map(area => (
                            <label key={area.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50">
                              <Checkbox 
                                checked={formData.service_area_ids?.includes(area.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setFormData({
                                      ...formData, 
                                      service_area_ids: [...(formData.service_area_ids || []), area.id]
                                    });
                                  } else {
                                    setFormData({
                                      ...formData, 
                                      service_area_ids: (formData.service_area_ids || []).filter(id => id !== area.id)
                                    });
                                  }
                                }}
                                data-testid={`service-area-checkbox-${area.id}`}
                              />
                              <span className="text-sm">{area.name}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Nenhuma área de serviço cadastrada.
                          <br />
                          <span className="text-xs">Cadastre áreas na página de Voluntários.</span>
                        </p>
                      )}
                      {formData.service_area_ids?.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                            type="button"
                            onClick={() => setFormData({...formData, service_area_ids: []})}
                          >
                            <X size={14} className="mr-1" />
                            Limpar seleção
                          </Button>
                        </div>
                      )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecione as áreas onde este adolescente serve como voluntário
                  </p>
                </div>
              </div>
              
              {/* Pequenos Grupos (somente leitura) */}
              {editingId && (
                <div className="col-span-2">
                  <Label className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-blue-600" />
                    Pequenos Grupos
                  </Label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[44px]">
                    {(() => {
                      const pgs = adolescentPgMap[editingId] || [];
                      if (pgs.length === 0) {
                        return (
                          <span className="text-gray-500 text-sm">
                            Não participa de nenhum PG
                          </span>
                        );
                      }
                      const regularPgs = pgs.filter(pg => !pg.is_temporary);
                      const temporaryPgs = pgs.filter(pg => pg.is_temporary);
                      return (
                        <div className="flex flex-wrap gap-2">
                          {regularPgs.map(pg => (
                            <span key={pg.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {pg.name}
                            </span>
                          ))}
                          {temporaryPgs.map(pg => (
                            <span key={pg.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {pg.name} (temp)
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Para alterar o PG, use a página de Pequenos Grupos
                  </p>
                </div>
              )}
              
              <AffinitySelector
                selectedIds={formData.affinity_ids}
                onChange={(ids) => setFormData({...formData, affinity_ids: ids})}
                excludeId={editingId}
              />
              
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancelar</Button>
                <Button type="submit" className="btn-primary" data-testid="adolescent-submit-button">{editingId ? 'Salvar Alterações' : 'Cadastrar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-6">
        <button 
          onClick={() => { clearFilters(); }}
          className={`card p-2 sm:p-3 text-center transition hover:shadow-md ${!hasActiveFilters ? 'ring-2 ring-blue-500' : ''}`}
        >
          <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.total}</p>
          <p className="text-xs text-gray-500">Total</p>
        </button>
        <button 
          onClick={() => { clearFilters(); setGenderFilter(['masculino']); }}
          className={`card p-2 sm:p-3 text-center transition hover:shadow-md ${genderFilter.length === 1 && genderFilter[0] === 'masculino' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.masculino}</p>
          <p className="text-xs text-blue-500">👦 Meninos</p>
        </button>
        <button 
          onClick={() => { clearFilters(); setGenderFilter(['feminino']); }}
          className={`card p-2 sm:p-3 text-center transition hover:shadow-md ${genderFilter.length === 1 && genderFilter[0] === 'feminino' ? 'ring-2 ring-pink-500' : ''}`}
        >
          <p className="text-xl sm:text-2xl font-bold text-pink-600">{stats.feminino}</p>
          <p className="text-xs text-pink-500">👧 Meninas</p>
        </button>
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              onClick={() => { clearFilters(); setEngagementFilter(['ativo']); }}
              className={`card p-2 sm:p-3 text-center transition hover:shadow-md ${engagementFilter.length === 1 && engagementFilter[0] === 'ativo' ? 'ring-2 ring-green-500' : ''}`}
            >
              <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.ativos}</p>
              <p className="text-xs text-green-500 flex items-center justify-center gap-1">✓ Ativos <Info size={10} /></p>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-green-900 text-white">
            <p className="font-semibold mb-1">Critérios para Ativo:</p>
            {engagementCriteria ? (
              <ul className="text-xs space-y-1">
                <li>• Taxa de presença ≥ {engagementCriteria.active_rate_threshold}%</li>
                <li>• OU presença nos últimos {engagementCriteria.recent_events_count} eventos</li>
              </ul>
            ) : (
              <p className="text-xs">Carregando critérios...</p>
            )}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              onClick={() => { clearFilters(); setEngagementFilter(['desengajado']); }}
              className={`card p-3 text-center transition hover:shadow-md ${engagementFilter.length === 1 && engagementFilter[0] === 'desengajado' ? 'ring-2 ring-amber-500' : ''}`}
            >
              <p className="text-2xl font-bold text-amber-600">{stats.desengajados}</p>
              <p className="text-xs text-amber-500 flex items-center justify-center gap-1">⚠ Desengajados <Info size={10} /></p>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-amber-900 text-white">
            <p className="font-semibold mb-1">Critérios para Desengajado:</p>
            {engagementCriteria ? (
              <ul className="text-xs space-y-1">
                <li>• Ausente há {engagementCriteria.disengaged_weeks}-{engagementCriteria.inactive_weeks - 1} semanas</li>
                <li>• OU menos de {engagementCriteria.min_yearly_attendance} presenças no ano</li>
              </ul>
            ) : (
              <p className="text-xs">Carregando critérios...</p>
            )}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              onClick={() => { clearFilters(); setEngagementFilter(['inativo']); }}
              className={`card p-3 text-center transition hover:shadow-md ${engagementFilter.length === 1 && engagementFilter[0] === 'inativo' ? 'ring-2 ring-red-500' : ''}`}
            >
              <p className="text-2xl font-bold text-red-600">{stats.inativos}</p>
              <p className="text-xs text-red-500 flex items-center justify-center gap-1">✗ Inativos <Info size={10} /></p>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-red-900 text-white">
            <p className="font-semibold mb-1">Critérios para Inativo:</p>
            {engagementCriteria ? (
              <ul className="text-xs space-y-1">
                <li>• Nunca participou de nenhum evento</li>
                <li>• OU ausente há {engagementCriteria.inactive_weeks}+ semanas</li>
              </ul>
            ) : (
              <p className="text-xs">Carregando critérios...</p>
            )}
          </TooltipContent>
        </Tooltip>
        <button 
          onClick={() => { clearFilters(); setStatusFilter(['membro']); }}
          className={`card p-3 text-center transition hover:shadow-md ${statusFilter.length === 1 && statusFilter[0] === 'membro' ? 'ring-2 ring-purple-500' : ''}`}
        >
          <p className="text-2xl font-bold text-purple-600">{stats.membros}</p>
          <p className="text-xs text-purple-500">Membros</p>
        </button>
        <button 
          onClick={() => { clearFilters(); setStatusFilter(['visitante', 'visitante_primeira_vez']); }}
          className={`card p-3 text-center transition hover:shadow-md ${statusFilter.includes('visitante') && !statusFilter.includes('membro') ? 'ring-2 ring-indigo-500' : ''}`}
        >
          <p className="text-2xl font-bold text-indigo-600">{stats.visitantes}</p>
          <p className="text-xs text-indigo-500">Visitantes</p>
        </button>
        
        {/* PG Stats - apenas ativos */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="card p-3 text-center bg-gradient-to-br from-purple-50 to-purple-100">
              <p className="text-2xl font-bold text-purple-700">{stats.activeInPg}</p>
              <p className="text-xs text-purple-600 flex items-center justify-center gap-1">Em PGs <Info size={10} /></p>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-purple-900 text-white">
            <p className="text-xs">Adolescentes ATIVOS que estão em algum Pequeno Grupo</p>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="card p-3 text-center bg-gradient-to-br from-orange-50 to-orange-100">
              <p className="text-2xl font-bold text-orange-700">{stats.activeWithoutPg}</p>
              <p className="text-xs text-orange-600 flex items-center justify-center gap-1">Sem PG <Info size={10} /></p>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs bg-orange-900 text-white">
            <p className="text-xs">Adolescentes ATIVOS que ainda não estão em nenhum Pequeno Grupo</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Search and Filters */}
      <div className="card mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Buscar por nome, CPF, telefone, nome da mãe/pai/responsável..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Limpar busca"
              >
                <X size={16} />
              </button>
            )}
            {search.trim() && (
              <p className="mt-2 text-sm text-gray-500">
                {isSearching
                  ? 'Buscando...'
                  : `${filteredAdolescents.length} resultado(s) para "${debouncedSearch}"`}
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-3">
            {/* Filtro Gênero */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Gênero</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-36 justify-between h-10">
                    <span className="truncate">
                      {genderFilter.length === 2 ? 'Todos' : 
                       genderFilter.length === 0 ? 'Nenhum' :
                       genderFilter.map(g => g === 'masculino' ? '👦' : '👧').join(' ')}
                    </span>
                    <ChevronDown size={16} className="ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-3">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={genderFilter.includes('masculino')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setGenderFilter([...genderFilter, 'masculino']);
                          } else {
                            setGenderFilter(genderFilter.filter(g => g !== 'masculino'));
                          }
                        }}
                      />
                      <span>👦 Meninos</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={genderFilter.includes('feminino')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setGenderFilter([...genderFilter, 'feminino']);
                          } else {
                            setGenderFilter(genderFilter.filter(g => g !== 'feminino'));
                          }
                        }}
                      />
                      <span>👧 Meninas</span>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Filtro Engajamento */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Engajamento</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-44 justify-between h-10">
                    <span className="truncate">
                      {engagementFilter.length === 3 ? 'Todos' : 
                       engagementFilter.length === 0 ? 'Nenhum' :
                       engagementFilter.map(e => e === 'ativo' ? '✓' : e === 'desengajado' ? '⚠' : '✗').join(' ')}
                    </span>
                    <ChevronDown size={16} className="ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-3">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={engagementFilter.includes('ativo')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEngagementFilter([...engagementFilter, 'ativo']);
                          } else {
                            setEngagementFilter(engagementFilter.filter(e => e !== 'ativo'));
                          }
                        }}
                      />
                      <span className="text-green-600">✓ Ativos</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={engagementFilter.includes('desengajado')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEngagementFilter([...engagementFilter, 'desengajado']);
                          } else {
                            setEngagementFilter(engagementFilter.filter(e => e !== 'desengajado'));
                          }
                        }}
                      />
                      <span className="text-yellow-600">⚠ Desengajados</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={engagementFilter.includes('inativo')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setEngagementFilter([...engagementFilter, 'inativo']);
                          } else {
                            setEngagementFilter(engagementFilter.filter(e => e !== 'inativo'));
                          }
                        }}
                      />
                      <span className="text-red-600">✗ Inativos</span>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Filtro Status */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Status</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-40 justify-between h-10">
                    <span className="truncate">
                      {statusFilter.length === 3 ? 'Todos' : 
                       statusFilter.length === 0 ? 'Nenhum' :
                       statusFilter.length + ' selecionado(s)'}
                    </span>
                    <ChevronDown size={16} className="ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-3">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={statusFilter.includes('membro')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setStatusFilter([...statusFilter, 'membro']);
                          } else {
                            setStatusFilter(statusFilter.filter(s => s !== 'membro'));
                          }
                        }}
                      />
                      <span>Membros</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={statusFilter.includes('visitante')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setStatusFilter([...statusFilter, 'visitante']);
                          } else {
                            setStatusFilter(statusFilter.filter(s => s !== 'visitante'));
                          }
                        }}
                      />
                      <span>Visitantes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox 
                        checked={statusFilter.includes('visitante_primeira_vez')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setStatusFilter([...statusFilter, 'visitante_primeira_vez']);
                          } else {
                            setStatusFilter(statusFilter.filter(s => s !== 'visitante_primeira_vez'));
                          }
                        }}
                      />
                      <span>Visitantes 1ª vez</span>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Filtro Idade */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Idade</label>
              <Select value={ageFilter} onValueChange={setAgeFilter}>
                <SelectTrigger className="w-28 h-10">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {uniqueAges.map(age => (
                    <SelectItem key={age} value={String(age)}>{age} anos</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtro PG */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Pequeno Grupo</label>
              <Select value={pgFilter} onValueChange={setPgFilter}>
                <SelectTrigger className="w-32 h-10">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="com_pg">Com PG</SelectItem>
                  <SelectItem value="sem_pg">Sem PG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {hasActiveFilters && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-transparent">.</label>
                <Button variant="outline" size="sm" onClick={clearFilters} className="flex items-center space-x-1 h-10">
                  <X size={16} />
                  <span>Limpar</span>
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {hasActiveFilters && (
          <div className="mt-3 text-sm text-gray-600">
            Mostrando <strong>{filteredAdolescents.length}</strong> de {stats.total} adolescentes
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12">Carregando...</div>
      ) : filteredAdolescents.length === 0 ? (
        <div className="card text-center py-12">
          <Users size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">Nenhum adolescente encontrado</p>
          {hasActiveFilters && (
            <Button variant="link" onClick={clearFilters} className="mt-2">Limpar filtros</Button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Indicador de ordenação */}
          {hasCustomSort && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <span className="text-sm text-blue-700">
                Ordenando por: <strong>
                  {sortColumn === 'name' ? 'Nome' : 
                   sortColumn === 'age' ? 'Idade' : 
                   sortColumn === 'phone' ? 'Telefone' : 
                   sortColumn === 'engagement' ? 'Frequência' : 
                   sortColumn === 'checkins' ? 'Presenças' : sortColumn}
                </strong> ({sortDirection === 'asc' ? '↑ A-Z' : '↓ Z-A'})
              </span>
              <Button variant="ghost" size="sm" onClick={clearSort} className="text-blue-600 hover:text-blue-800">
                <X size={14} className="mr-1" />
                Limpar ordenação
              </Button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="table w-full" data-testid="adolescents-table">
              <thead>
                <tr>
                  <th 
                    className="cursor-pointer hover:bg-gray-100 select-none transition"
                    onClick={() => handleSort('name')}
                    data-testid="sort-name"
                  >
                    <div className="flex items-center">
                      Nome
                      {renderSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-100 select-none transition"
                    onClick={() => handleSort('age')}
                    data-testid="sort-age"
                  >
                    <div className="flex items-center">
                      Idade
                      {renderSortIcon('age')}
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-100 select-none transition"
                    onClick={() => handleSort('phone')}
                    data-testid="sort-phone"
                  >
                    <div className="flex items-center">
                      Telefone
                      {renderSortIcon('phone')}
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-100 select-none transition"
                    onClick={() => handleSort('engagement')}
                    data-testid="sort-engagement"
                  >
                    <div className="flex items-center">
                      Frequência
                      {renderSortIcon('engagement')}
                    </div>
                  </th>
                  <th 
                    className="cursor-pointer hover:bg-gray-100 select-none transition"
                    onClick={() => handleSort('checkins')}
                    data-testid="sort-checkins"
                  >
                    <div className="flex items-center">
                      Presenças
                      {renderSortIcon('checkins')}
                    </div>
                  </th>
                  <th>Voluntariado</th>
                  <th>Pequeno Grupo</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredAdolescents.map((adolescent) => (
                  <tr key={adolescent.id} data-testid="adolescent-row">
                    <td>
                      <div className="flex items-center space-x-3">
                        {adolescent.photo_url ? (
                          <img src={adolescent.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <User size={16} className="text-gray-500" />
                          </div>
                        )}
                        <span className="font-medium">{highlightMatch(adolescent.name, debouncedSearch)}</span>
                      </div>
                    </td>
                    <td>{adolescent.age} anos</td>
                    <td>{adolescent.phone ? highlightMatch(adolescent.phone, debouncedSearch) : '-'}</td>
                    <td>{getEngagementBadge(adolescent.engagement_status)}</td>
                    <td>
                      <span className="text-sm">
                        {adolescent.total_checkins || 0}
                        {adolescent.attendance_rate !== undefined && (
                          <span className={`ml-1 text-xs ${adolescent.attendance_rate >= 50 ? 'text-green-600' : 'text-red-600'}`}>
                            ({adolescent.attendance_rate}%)
                          </span>
                        )}
                      </span>
                    </td>
                    <td>
                      {renderServiceAreas(adolescent)}
                    </td>
                    <td>
                      {renderPgInfo(adolescent)}
                    </td>
                    <td>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewProfile(adolescent.id)} className="text-blue-600" data-testid="view-profile-button">
                          <User size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(adolescent)} className="text-gray-600" data-testid="edit-button">
                          <Edit size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(adolescent.id)} className="text-red-600" data-testid="delete-button">
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Profile Dialog */}
      <AdolescentProfile
        adolescentId={selectedAdolescentId}
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
      />
    </div>
    </TooltipProvider>
  );
};

export default Adolescents;
